import { reject } from 'ramda';
import moment from 'moment';

import { getPickupKind, isSaturdayService, isSundayService } from './model';
import {
  ALL_PART_NONE_OPTIONS_VALUES,
  DELIVERY_DAY_OPTIONS_VALUES,
  EXCEPTION_TYPES_VALUES,
  YES_NO_OPTIONS_VALUES,
  PROPERTY_TYPE_OPTIONS_VALUES,
  REQUEST_OPTIONS_VALUES,
  CRITERIA,
} from '../../components/modals/SelectByCriteria/constants';
import {
  hasAnyExceptions,
  hasException,
  ignoreServiceException,
  isPremium,
} from '../../models/routeDetail';
import { formatTimeString } from '../../components/modals/SelectByCriteria/model';
import { dateToNumber, toSeconds, toMinutes } from '../../utils/date';
import { isCollectionStop } from '../../models/stop';
import { removeWhiteSpace } from '../../utils/string';
import { pickupKindTypes } from '../../enums/stopTypes';

export const removeCriteriaById = (criteria, id) =>
  reject((o) => o.id === id, criteria);

const applyCadCriteria = ({ value }, stop) => {
  if (!isCollectionStop(stop)) {
    switch (value) {
      case ALL_PART_NONE_OPTIONS_VALUES.ALL:
        return stop.totalParcels && stop.totalCadParcels === stop.totalParcels;
      case ALL_PART_NONE_OPTIONS_VALUES.PART:
        return (
          stop.totalCadParcels > 0 && stop.totalCadParcels < stop.totalParcels
        );
      case ALL_PART_NONE_OPTIONS_VALUES.NONE:
        return stop.totalCadParcels === 0;
      default:
        break;
    }
  }
  return false;
};

const applyDeliveryDayCriteria = ({ value }, stop) => {
  switch (value) {
    case DELIVERY_DAY_OPTIONS_VALUES.SATURDAY:
      return isSaturdayService(stop);
    case DELIVERY_DAY_OPTIONS_VALUES.SUNDAY:
      return isSundayService(stop);
    case DELIVERY_DAY_OPTIONS_VALUES.BOTH:
      return isSaturdayService(stop) || isSundayService(stop);
    case DELIVERY_DAY_OPTIONS_VALUES.NONE:
      return !isSaturdayService(stop) && !isSundayService(stop);
    default:
      break;
  }
};

const applyExceptionCriteria = ({ value }, stop) => {
  switch (value) {
    case ALL_PART_NONE_OPTIONS_VALUES.ALL:
      return hasAnyExceptions(stop);
    case ALL_PART_NONE_OPTIONS_VALUES.NONE:
      return !hasAnyExceptions(stop);
    case EXCEPTION_TYPES_VALUES.POSTCODE:
      return hasException(stop, 1);
    case EXCEPTION_TYPES_VALUES.SERVICE:
      return hasException(stop, 2) && !ignoreServiceException(stop);
    case EXCEPTION_TYPES_VALUES.DUTY:
      return hasException(stop, 3);
    case EXCEPTION_TYPES_VALUES.RANGE:
      return hasException(stop, 6);
    case EXCEPTION_TYPES_VALUES.LEGAL_DUTY:
      return hasException(stop, 7);
    default:
      break;
  }
};

const applyHubLastScannedInCriteria = ({ value }, stop) =>
  stop.parcel?.some((parcel) => parcel.hubLastSeen === value);

const applyLoadedCriteria = ({ value }, stop) => {
  if (!isCollectionStop(stop)) {
    switch (value) {
      case ALL_PART_NONE_OPTIONS_VALUES.ALL:
        return stop.totalParcels && stop.totalLoaded === stop.totalParcels;
      case ALL_PART_NONE_OPTIONS_VALUES.PART:
        return stop.totalLoaded > 0 && stop.totalLoaded < stop.totalParcels;
      case ALL_PART_NONE_OPTIONS_VALUES.NONE:
        return stop.totalLoaded === 0;
      default:
        break;
    }
  }
  return false;
};

const applyPickupKindCriteria = ({ value }, stop) => {
  const stopPickupKind = getPickupKind({
    parcel: isCollectionStop(stop) ? [stop.collection] : stop.parcel,
  });
  switch (value) {
    case pickupKindTypes.shop:
      return stopPickupKind === pickupKindTypes.shop;
    case pickupKindTypes.locker:
      return stopPickupKind === pickupKindTypes.locker;
    default:
      break;
  }

  return false;
};

const applyPerishableCriteria = ({ value }, stop) => {
  if (value === YES_NO_OPTIONS_VALUES.YES) {
    return stop.perishable === true;
  }
  return stop.perishable === false;
};

const applyPostcodeCriteria = ({ value }, stop) =>
  removeWhiteSpace(stop.stopAddress.postcode).includes(
    removeWhiteSpace(value.toUpperCase())
  );

const applyOperatorToValues = (operator, leftOperand, rightOperand) => {
  switch (operator) {
    case '>':
      return leftOperand > rightOperand;
    case '>=':
      return leftOperand >= rightOperand;
    case '=':
      return leftOperand === rightOperand;
    case '<':
      return leftOperand < rightOperand;
    case '<=':
      return leftOperand <= rightOperand;
    default:
      break;
  }
};

const applyEtaCriteria = ({ value, operator }, stop) => {
  const valueFormatted = formatTimeString(value);

  return applyOperatorToValues(
    operator,
    toMinutes(stop.eta),
    toMinutes(valueFormatted)
  );
};

const applyHubScanDateTimeCriteria = ({ value, operator }, stop) => {
  const inputValue = dateToNumber(value.toString());

  return stop.parcel?.some((parcel) => {
    if (!parcel.hubScanDateTime) {
      return false;
    }

    const parcelHubScanDateTime = dateToNumber(parcel.hubScanDateTime);

    return applyOperatorToValues(operator, parcelHubScanDateTime, inputValue);
  });
};

const applyParcelAgeCriteria = ({ value, operator }, stop) =>
  stop.parcel &&
  stop.parcel?.some((parcel) => {
    if (!parcel.targetDeliveryDate) {
      return false;
    }

    const parcelAge = moment(parcel.targetDeliveryDate, 'YYYY-MM-DD').diff(
      moment(),
      'days'
    );

    return applyOperatorToValues(operator, parcelAge, value);
  });

const applyParcelsNoOfCriteria = ({ value, operator }, stop) =>
  applyOperatorToValues(operator, stop.totalParcels, value);

const applyPreciseCriteria = ({ value }, stop) => {
  if (value === YES_NO_OPTIONS_VALUES.YES) {
    return stop.stopTypeDescription === 'T';
  }
  return stop.stopTypeDescription !== 'T';
};

const applyPremiumsCriteria = ({ value }, stop) =>
  value === YES_NO_OPTIONS_VALUES.YES ? isPremium(stop) : !isPremium(stop);

const applyPriorityCriteria = ({ value, operator }, stop) =>
  applyOperatorToValues(operator, stop.priority, value);

const applyPropertyTypeCriteria = ({ value }, stop) =>
  stop.parcel &&
  stop.parcel?.some((parcel) =>
    value === PROPERTY_TYPE_OPTIONS_VALUES.B2B
      ? parcel.propertyType === 'B'
      : parcel.propertyType === 'R'
  );

const applyRequestCriteria = ({ value }, stop) => {
  const { requests = [] } = stop;
  return value === REQUEST_OPTIONS_VALUES.OPEN
    ? !!requests.length
    : !requests.length;
};

const applySmallsCriteria = ({ value }, stop) =>
  value === YES_NO_OPTIONS_VALUES.YES
    ? stop.totalSmalls > 0
    : stop.totalSmalls === 0;

const applyOpportunityTime = ({ value, operator }, stop) =>
  applyOperatorToValues(
    operator,
    stop?.opportunityTime || 0,
    toSeconds(formatTimeString(value))
  );

const applyTimeCriteria =
  (property) =>
  ({ value, operator }, stop) => {
    const valueFormatted = formatTimeString(value);
    let stopProperty =
      typeof stop[property] === 'number'
        ? moment().hour(0).minute(stop[property]).format('HH:mm')
        : stop[property];

    return applyOperatorToValues(
      operator,
      toSeconds(stopProperty),
      toSeconds(valueFormatted)
    );
  };

const applyStopTypeCriteria = ({ value }, stop) => stop.stopType === value;

const applyManifestLocationCriteria = ({ value }, stop) =>
  stop.parcel?.some(
    (parcel) =>
      parcel.manifestLocation &&
      parcel.manifestLocation.toUpperCase() ===
        removeWhiteSpace(value.toUpperCase())
  );

export const criteriaMapper = {
  [CRITERIA.CADS]: applyCadCriteria,
  [CRITERIA.DELIVERY_DAY]: applyDeliveryDayCriteria,
  [CRITERIA.ETA]: applyEtaCriteria,
  [CRITERIA.EXCEPTION]: applyExceptionCriteria,
  [CRITERIA.HUB_LAST_SCANNED_IN]: applyHubLastScannedInCriteria,
  [CRITERIA.HUB_SCAN_DATE_TIME]: applyHubScanDateTimeCriteria,
  [CRITERIA.LOADED]: applyLoadedCriteria,
  [CRITERIA.PICKUP_KIND]: applyPickupKindCriteria,
  [CRITERIA.OPPORTUNITY_TIME]: applyOpportunityTime,
  [CRITERIA.PARCEL_AGE]: applyParcelAgeCriteria,
  [CRITERIA.PARCELS_NO_OF]: applyParcelsNoOfCriteria,
  [CRITERIA.PERISHABLE]: applyPerishableCriteria,
  [CRITERIA.POSTCODE]: applyPostcodeCriteria,
  [CRITERIA.PRECISE]: applyPreciseCriteria,
  [CRITERIA.PREMIUMS]: applyPremiumsCriteria,
  [CRITERIA.PRIORITY]: applyPriorityCriteria,
  [CRITERIA.PROPERTY_TYPE]: applyPropertyTypeCriteria,
  [CRITERIA.REQUEST]: applyRequestCriteria,
  [CRITERIA.SMALLS]: applySmallsCriteria,
  [CRITERIA.STOP_TIME]: applyTimeCriteria('stopTime'),
  [CRITERIA.STOP_TYPE]: applyStopTypeCriteria,
  [CRITERIA.MANIFEST_LOCATION]: applyManifestLocationCriteria,
};
